<!-- Desktop-only welcome + region picker. Network-silent: touches only theme
     (local DOM) and region (localStorage). -->
<div class="welcome" [attr.data-step]="step()">

  <!-- Animated, paper-themed SVG backdrop (decorative). Disabled under
       prefers-reduced-motion via CSS. -->
  <svg class="welcome__bg" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice"
       aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="wgA" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="var(--color-primary)" stop-opacity="0.55" />
        <stop offset="100%" stop-color="var(--color-primary)" stop-opacity="0" />
      </radialGradient>
      <radialGradient id="wgB" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="var(--text)" stop-opacity="0.10" />
        <stop offset="100%" stop-color="var(--text)" stop-opacity="0" />
      </radialGradient>
      <linearGradient id="wgLine" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="var(--color-primary)" stop-opacity="0.0" />
        <stop offset="50%" stop-color="var(--color-primary)" stop-opacity="0.35" />
        <stop offset="100%" stop-color="var(--color-primary)" stop-opacity="0.0" />
      </linearGradient>
    </defs>

    <!-- Drifting gradient orbs -->
    <circle class="orb orb--a" cx="320" cy="260" r="360" fill="url(#wgA)" />
    <circle class="orb orb--b" cx="1140" cy="640" r="420" fill="url(#wgA)" />
    <circle class="orb orb--c" cx="820" cy="180" r="300" fill="url(#wgB)" />

    <!-- Flowing contour lines (printer/paper homage) -->
    <g class="lines" fill="none" stroke="url(#wgLine)" stroke-width="1.5">
      <path class="line line--1" d="M-100 300 C 360 180, 1080 420, 1540 280" />
      <path class="line line--2" d="M-100 460 C 420 360, 1020 560, 1540 440" />
      <path class="line line--3" d="M-100 620 C 380 540, 1100 700, 1540 600" />
    </g>
  </svg>

  <!-- Draggable title strip (no glass bar on this surface) -->
  <div class="welcome__drag" data-tauri-drag-region></div>

  <!-- Glass panel -->
  <section class="welcome__panel glass" role="dialog" aria-label="Welcome to RICOH Workplace">

    @switch (step()) {

      <!-- ── Step 0 · Hero ───────────────────────────────────── -->
      @case (0) {
        <div class="step step--hero" data-testid="welcome-hero">
          <p class="eyebrow">RICOH Workplace</p>
          <h1 class="hero__title">Welcome to your workplace, on the desktop.</h1>
          <p class="hero__sub">
            A faster, native way to find space, book rooms and stay on top of your
            day — set it up in a few seconds.
          </p>
          <div class="step__actions">
            <paper-button intent="primary" size="lg" data-testid="welcome-start"
              (click)="next()">
              Get started
              <paper-icon name="chevron-right" [size]="16" aria-hidden="true" />
            </paper-button>
          </div>
        </div>
      }

      <!-- ── Step 1 · Appearance ─────────────────────────────── -->
      @case (1) {
        <div class="step step--appearance" data-testid="welcome-appearance">
          <p class="eyebrow">Make it yours</p>
          <h2 class="step__title">Choose a theme</h2>
          <p class="step__sub">You can change this anytime in your profile.</p>

          <div class="theme-grid" role="radiogroup" aria-label="Theme">
            @for (t of themeOptions; track t.name) {
              <button type="button" class="theme-tile"
                [class.is-active]="activeTheme() === t.name"
                role="radio" [attr.aria-checked]="activeTheme() === t.name"
                [attr.data-testid]="'welcome-theme-' + t.name"
                (click)="pickTheme(t.name)">
                <span class="theme-tile__swatch" [attr.data-theme]="t.name">
                  <span class="sw sw--bg"></span>
                  <span class="sw sw--accent"></span>
                  <span class="sw sw--text"></span>
                </span>
                <span class="theme-tile__label">{{ t.label }}</span>
                @if (activeTheme() === t.name) {
                  <span class="theme-tile__check"><paper-icon name="check" [size]="12" aria-hidden="true" /></span>
                }
              </button>
            }
          </div>

          <div class="step__actions">
            <paper-button intent="ghost" size="md" (click)="back()">Back</paper-button>
            <paper-button intent="primary" size="md" data-testid="welcome-appearance-next"
              (click)="next()">
              Continue
              <paper-icon name="chevron-right" [size]="16" aria-hidden="true" />
            </paper-button>
          </div>
        </div>
      }

      <!-- ── Step 2 · Region ─────────────────────────────────── -->
      @case (2) {
        <div class="step step--region" data-testid="welcome-region">
          <p class="eyebrow">Data region</p>
          <h2 class="step__title">Where should your data live?</h2>
          <p class="step__sub">
            Choose the region closest to your organisation. Your data stays in the
            region you pick — nothing is sent anywhere until you choose.
          </p>

          <div class="region-grid" role="list">
            @for (r of regions; track r.id) {
              <button type="button" class="region-card" role="listitem"
                [disabled]="committing()"
                [attr.data-testid]="'welcome-region-' + r.id"
                (click)="chooseRegion(r.id)">
                <span class="region-card__icon">
                  <paper-icon name="location-pin" [size]="22" aria-hidden="true" />
                </span>
                <span class="region-card__body">
                  <span class="region-card__label">{{ r.label }}</span>
                  <span class="region-card__residency">{{ r.residency }}</span>
                </span>
                <span class="region-card__go">
                  <paper-icon name="chevron-right" [size]="18" aria-hidden="true" />
                </span>
              </button>
            }
          </div>

          <div class="step__actions">
            <paper-button intent="ghost" size="md" [disabled]="committing()"
              (click)="back()">Back</paper-button>
            @if (committing()) {
              <span class="committing" aria-live="polite">Setting up your region…</span>
            }
          </div>
        </div>
      }
    }

    <!-- Progress dots -->
    <nav class="dots" aria-label="Setup progress">
      @for (i of steps; track i) {
        <button type="button" class="dot" [class.is-active]="step() === i"
          [attr.aria-label]="'Step ' + (i + 1)" [attr.aria-current]="step() === i"
          [disabled]="committing()" (click)="goTo(i)"></button>
      }
    </nav>
  </section>
</div>
